import iconsPng from '@/assets/img/png/icons';
import { formatDomainLabel } from '@/constants/contentDomains';
import { TContentDomain } from '@/types/Content.types';
import { THealthDomain } from '@/types/Health.types';
import { Box, HStack, Image } from '@chakra-ui/react';

// `purple` is the feed-card treatment (purple glyph on a pale purple tile). `muted` is the
// loop's meta row: a white glyph on a solid grey-blue tile. `overlay` is for sitting on a
// colored/photo surface — a white glyph on a translucent tile that reads on any backdrop.
export type TDomainIconTileVariant = 'purple' | 'muted' | 'overlay';

// The domain artwork ships as purple and grey PNGs only — there is no white set. Rather
// than add a third set of assets, the white variants recolour the existing one:
// brightness(0) flattens the glyph to solid black while preserving its alpha, and
// invert(1) turns that black into white — a clean white silhouette over any tile colour.
const WHITE_GLYPH_FILTER = { filter: 'brightness(0) invert(1)' };

const VARIANT_STYLES: Record<TDomainIconTileVariant, { backgroundColor: string; iconSx?: Record<string, string> }> = {
  purple: { backgroundColor: 'background.lightPurple' },
  muted: { backgroundColor: 'background.blueGray', iconSx: WHITE_GLYPH_FILTER },
  overlay: { backgroundColor: 'whiteAlpha.500', iconSx: WHITE_GLYPH_FILTER },
};

// Compact row of selected-domain icon tiles (the thumbnail/feed card overlay, and the
// loop's published-meta row).
const DomainIconTiles = ({
  domains,
  max = 7,
  variant = 'purple',
}: {
  domains: TContentDomain[];
  max?: number;
  variant?: TDomainIconTileVariant;
}) => {
  const { backgroundColor, iconSx } = VARIANT_STYLES[variant];

  return (
    <HStack gap="6px">
      {domains.slice(0, max).map((domain) => (
        <Box key={domain} backgroundColor={backgroundColor} padding="5px" borderRadius="8px">
          <Image
            src={iconsPng.wellnessDomainsPurple[domain as THealthDomain]}
            width="16px"
            height="16px"
            alt={formatDomainLabel(domain)}
            sx={iconSx}
          />
        </Box>
      ))}
    </HStack>
  );
};

export default DomainIconTiles;
